import React, { useEffect, useState, useRef } from "react";
import { Send, Plus, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/Field";

export default function Messages({ admin = false }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef();

  const loadConvos = () => api.get("/conversations").then((r) => setConvos(r.data)).catch(() => {});
  useEffect(() => { loadConvos(); }, []);
  useEffect(() => {
    if (!active) return;
    api.get(`/conversations/${active.id}/messages`).then((r) => setMsgs(r.data)).catch(() => {});
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      const { data } = await api.post(`/conversations/${active.id}/messages`, { body: text });
      setMsgs([...msgs, data]); setText(""); loadConvos();
    } catch (err) { toast.error(apiErr(err)); }
  };
  const newConvo = async () => {
    const subject = prompt(lang === "de" ? "Betreff:" : "Subject:");
    if (!subject) return;
    const { data } = await api.post("/conversations", { subject });
    loadConvos(); setActive(data);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-black">{t("portal.messages")}</h1>
        {!admin && <button onClick={newConvo} className="btn-primary !py-2 !px-4 text-sm" data-testid="new-convo"><Plus size={16} /> {t("portal.newMessage")}</button>}
      </div>
      <div className="grid lg:grid-cols-3 gap-4 h-[70vh]">
        <div className="surface overflow-y-auto">
          {convos.length === 0 && <p className="p-6 text-sm text-muted-foreground">{t("common.none")}</p>}
          {convos.map((c) => (
            <button key={c.id} onClick={() => setActive(c)} data-testid={`convo-${c.id}`}
              className={`w-full text-left p-4 border-b border-border hover:bg-secondary transition-colors ${active?.id === c.id ? "bg-secondary" : ""}`}>
              <div className="font-semibold text-sm truncate">{c.subject}</div>
              {admin && <div className="text-xs text-muted-foreground">{c.customer_name}</div>}
              <div className="text-xs text-muted-foreground truncate mt-0.5">{c.last_message || "—"}</div>
            </button>
          ))}
        </div>
        <div className="lg:col-span-2 surface flex flex-col">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
              <MessageSquare size={40} className="mb-3 opacity-40" />
              {lang === "de" ? "Unterhaltung auswählen" : "Select a conversation"}
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-border font-semibold">{active.subject}</div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {msgs.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                        {!mine && <div className="text-xs font-semibold mb-0.5 opacity-70">{m.sender_name}</div>}
                        {m.body}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-border flex gap-2">
                <Input testid="msg-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={lang === "de" ? "Nachricht…" : "Message…"} />
                <button className="btn-primary !px-4" data-testid="msg-send"><Send size={16} /></button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
